import { useStableObject } from '@atlassian/post-office-context';
import { MessageRendererProvider } from '@post-office/placement-contexts/message-renderer-provider';
import { MessageViewed } from '@post-office/placement-contexts/message-viewed-component';
import { useWaitForTrigger } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import { isClientTriggeredMessage } from '@post-office/placement-contexts/utils/is-client-triggered-message';
import type { TriggerType } from '@post-office/message-template-contracts/types';
import { useEffect } from 'react';

import { createPlacementComponentV2 } from '../create-placement/create-placement-component';
import type {
	PlacementConfig,
	PlacementLocation,
	PlacementMessage,
} from '../create-placement/shared-types';
import { PlacementMessageError, createPlacementMessageError } from './error';
import type {
	AnyFrontendStages,
	AnyProps,
	AnyValidatedFrontendStages,
	CreateFrontendPlacementMessageExecuteConfig,
	CreateFrontendPlacementMessageRunner,
	DefaultFrontendStages,
	NoProps,
	PlacementMessageFrontendFactory,
	PlacementRenderer,
} from '../create-placement/frontend-types';

export const createPlacementFrontend =
	<T extends AnyFrontendStages = DefaultFrontendStages>(placementConfig: PlacementConfig) =>
	<U extends AnyProps = NoProps>(
		placementRenderer: PlacementRenderer<T, U>,
	): PlacementMessageFrontendFactory<T, U> => ({
		createFrontendStage: (stage) => stage as never,

		createMessageFrontend: (stage) => (config) => [config.messageTemplateId, stage],

		createPlacementFrontendComponent: (config) => {
			const messageTemplateRouter = createFrontendPlacementMessageTemplateRouter({
				...placementConfig,
				...config,
			});

			return createPlacementComponentV2(
				placementConfig.placementId,
				placementRenderer(messageTemplateRouter),
				placementConfig.options,
			);
		},
	});

const createFrontendPlacementMessageRunner: CreateFrontendPlacementMessageRunner =
	({ messageTemplates, ...config }) =>
	({ messageTemplateId, messageInstanceId, messageCategory, children, ...props }) => {
		const renderableStage = messageTemplates?.[messageTemplateId];

		const isHeadless = config.headlessMessageTemplateIds?.includes(messageTemplateId);

		const placementLocation: PlacementLocation = {
			messageTemplateId,
			messageInstanceId,
			placementLevel: 'Message',
			stageName: config?.stageName,
			placementId: config.placementId,
		};

		useEffect(() => {
			if (isHeadless && renderableStage) {
				// eslint-disable-next-line no-console
				console.error(
					new PlacementMessageError({
						...placementLocation,
						...config,
					}),
					'Misconfiguration: message is registered as both headless and has a MessageFrontend renderer. Remove it from headlessMessageTemplateIds or remove the MessageFrontend export.',
				);
			}

			if (!isHeadless && !renderableStage) {
				// eslint-disable-next-line no-console
				console.error(
					new PlacementMessageError({ ...placementLocation, ...config }),
					'Misconfiguration: message is not registered as headless but has no MessageFrontend renderer. Register it as a headless message by reinstalling or add a MessageFrontend export.',
				);
			}
		}, [isHeadless, placementLocation, renderableStage, config]);

		const componentPropsStable = useStableObject(props);
		const messageProps = props as {
			triggerType?: TriggerType;
			context?: { triggerType?: TriggerType };
		};
		const triggerType = messageProps.triggerType ?? messageProps.context?.triggerType;
		const hasTrigger = isClientTriggeredMessage(triggerType);

		const { waitForTrigger } = useWaitForTrigger({
			hasTrigger,
			messageSuccessComponent: undefined,
			messageInstanceId,
			messageTemplateId,
			messageCategory,
		});

		if (!renderableStage) {
			return null;
		}

		try {
			renderableStage.validate({ messageTemplateId, children, ...props });
			const { Component } = renderableStage;

			return (
				<MessageRendererProvider
					{...componentPropsStable}
					messageTemplateId={messageTemplateId}
					messageInstanceId={messageInstanceId}
					messageCategory={messageCategory}
					isUFOEnabled={false}
					componentProps={componentPropsStable}
					waitForTrigger={waitForTrigger}
				>
					{!waitForTrigger && <MessageViewed />}
					<Component {...props} waitForTrigger={waitForTrigger}>
						{children}
					</Component>
				</MessageRendererProvider>
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(createPlacementMessageError(error));

			return null;
		}
	};

export const createFrontendPlacementMessageTemplateRouter = <T extends AnyFrontendStages>(
	config: CreateFrontendPlacementMessageExecuteConfig<T>,
): T => {
	const messageTemplateLookup = createMessageTemplateByStageLookup(config);

	return Object.entries(messageTemplateLookup).reduce(
		(base, [stageName, messageTemplates]) => ({
			...base,
			[stageName]: createFrontendPlacementMessageRunner({
				messageTemplates,
				stageName,
				placementId: config.placementId,
				headlessMessageTemplateIds: config.headlessMessageTemplateIds,
			}),
		}),
		{},
	) as unknown as T;
};

export const createMessageTemplateByStageLookup = <T extends AnyValidatedFrontendStages>(config: {
	messageTemplates: Array<PlacementMessage<T>>;
}): Record<string, AnyValidatedFrontendStages> => {
	const result: Record<string, AnyValidatedFrontendStages> = {};

	config.messageTemplates.forEach(([messageTemplateId, stage]) => {
		Object.entries(stage).forEach(([stageName, stageExecutable]) => {
			if (!result[stageName]) result[stageName] = {};
			result[stageName][messageTemplateId] = stageExecutable;
		});
	});

	return result as Record<string, T>;
};
